import type { TreeNodeDefinition, TreeParams, TreePath } from './tree.model';
import { binaryPathToTreePath, countB, generatePaths, pathToTerm } from './tree.util';

/**
 * Build a complete binary tree of depth `n` for the binomial expansion (a+b)^n.
 * Leaf nodes are labelled with their path term (e.g. "a²b").
 * Edge labels are "a" (left) and "b" (right).
 */
export function binomialTreeParams(n: number, _k?: number): TreeParams {
  function buildNode(depth: number, currentPath: number[]): TreeNodeDefinition {
    const id = currentPath.length === 0 ? 'root' : `n-${currentPath.join('-')}`;

    if (depth === 0) {
      // Leaf: label with the path term using unicode superscripts
      const binaryPath = currentPath.map((i) => (i === 0 ? 'a' : 'b')) as Array<'a' | 'b'>;
      const label = pathToTerm(binaryPath);
      return { id, content: { label } };
    }

    return {
      id,
      children: [
        { label: 'a', node: buildNode(depth - 1, [...currentPath, 0]) },
        { label: 'b', node: buildNode(depth - 1, [...currentPath, 1]) },
      ],
    };
  }

  const root = buildNode(n, []);
  return { root };
}

/**
 * Compute the matching TreePaths for a binomial tree where exactly `k`
 * branches are 'b' (child index 1). Used to highlight matching paths
 * in pascal's triangle integration.
 */
export function binomialMatchingPaths(n: number, k: number): TreePath[] {
  return generatePaths(n)
    .filter((p) => countB(p) === k)
    .map(binaryPathToTreePath);
}
